import { useNavigate } from 'react-router';
import { HomeModelsResponseData } from '../../models/homes.models.tsx';
import { useState } from 'react';
import { Box, Button, Card, Fade, IconButton, Typography } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { ImageCarousel } from '../ImageCarousel/ImageCarousel.tsx';
import { FavoriteButton } from '../FavoriteButton/FavoriteButton.tsx';
import { HomeModelFavoriteProperties } from '../../models/favorites.model.ts';
import { ShareModal } from '../../components/share-modal/share-modal.tsx';
import {
  cloudinaryImageThumbnailConfig,
  devOnlyCloudinaryInstance,
} from '../../utils/images.tsx';
import '../../unstructured-pages/SearchPage/possibly-stateful-components/results-list/results-list.scss';
import { useTimedState } from '../../hooks/use-timed-state.ts';

interface ResultHomeModelsProps {
  result: HomeModelsResponseData;
  homeModelFavorites?: HomeModelFavoriteProperties[] | undefined;
  showShareButton?: boolean;
  userId?: string | undefined;
  customizationQuery?: string;
}

export const ResultHomeModels = (props: ResultHomeModelsProps) => {
  const {
    showShareButton = true,
    customizationQuery,
    result: HomeModelsResponseData,
    homeModelFavorites,
  } = props;

  const [showingShareModal, setShowingShareModal] = useState(false);
  const navigate = useNavigate();

  const [showFavoriteMessage, setShowFavoriteMessage] = useTimedState(
    false,
    2500
  );

  const handleAddToFavorites = () => {
    setShowFavoriteMessage(true);
  };

  const navigateToPanorama = () => {
    const query = customizationQuery
      ? customizationQuery
      : `/panorama?homeId=${props.result?.sku}&currentSkuIds={}`;
    if (props.result?.sku) {
      void navigate(query);
    }
  };

  return (
    <Card className="results-list-item" elevation={0}>
      <Box className="options-container">
        {showShareButton && (
          <IconButton
            aria-label="share"
            color="primary"
            className="share-button"
            onClick={() => {
              setShowingShareModal(true);
            }}
          >
            <div className="button-tooltip">Share</div>
            <IosShareIcon sx={{ color: '#49454F' }} />
          </IconButton>
        )}
        {/* // If home model. Not home design */}
        {!customizationQuery ? (
          <FavoriteButton
            id={HomeModelsResponseData.id.toString() || ''}
            homeModelFavorites={homeModelFavorites}
            addtoFavoritesFunc={handleAddToFavorites}
            saveHomeModelResult={
              {
                sk: HomeModelsResponseData.id.toString(),
              } as HomeModelFavoriteProperties
            }
          />
        ) : (
          // The home designs that are in this page will always be favorites
          // but there are not necesary the same as favorited home models
          <FavoriteButton
            id={HomeModelsResponseData.id.toString() || ''}
            isfavoriteHomeDesign={true}
          />
        )}
      </Box>
      {HomeModelsResponseData.images?.length == 1 ? (
        <LazyLoadImage
          alt="property"
          effect="blur"
          src={cloudinaryImageThumbnailConfig(
            devOnlyCloudinaryInstance,
            HomeModelsResponseData.images[0]?.home_image_id
          )}
          width="334px"
          height="254px"
          style={{ objectFit: 'cover' }}
          useIntersectionObserver={true}
          onClick={() =>
            void navigate(`/models/details?id=${HomeModelsResponseData.id}`)
          }
        />
      ) : (
        <ImageCarousel
          homeModelsResponseData={HomeModelsResponseData}
          mlsImage={cloudinaryImageThumbnailConfig(
            devOnlyCloudinaryInstance,
            HomeModelsResponseData.images[0]?.home_image_id
          )}
          showArrows={true}
          smallArrows={true}
          onClick={() =>
            void navigate(`/models/details?id=${HomeModelsResponseData.id}`)
          }
          highResolutionImages={false}
        />
      )}
      <Box className="info-home-model">
        <Box
          onClick={() =>
            void navigate(`/models/details?id=${HomeModelsResponseData.id}`)
          }
        >
          <Typography variant="h3" className="name">
            {HomeModelsResponseData.name}
          </Typography>
          <Box className="lot-size">
            <Typography>{`${HomeModelsResponseData.bedrooms} Bed | ${HomeModelsResponseData.bathrooms} Bath | ${HomeModelsResponseData.square_feet} sqft.`}</Typography>
          </Box>
          <Typography className="text-light price-range">
            {`Starting from $${HomeModelsResponseData.price_min} - $${HomeModelsResponseData.price_max}`}
          </Typography>
        </Box>
        <Box className="card-button">
          <Button onClick={() => navigateToPanorama()} variant="contained">
            Customize my home
          </Button>
        </Box>
      </Box>
      <ShareModal
        isOpen={showingShareModal}
        setIsOpen={setShowingShareModal}
        selectedPropertyId={`models/details?id=${HomeModelsResponseData.id.toString()}`}
      />
      <Box className="favorite-alert">
        <Fade in={showFavoriteMessage} timeout={{ enter: 500, exit: 500 }}>
          <Button variant="contained" sx={{ cursor: 'default' }}>
            Favorite added to My Portal
          </Button>
        </Fade>
      </Box>
    </Card>
  );
};
